import { useState, useEffect } from "react";
import { Mail, Phone, Clock, Loader } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useAuth } from "@/context/AuthContext";
import { getMerchantProductCatalogue } from "@/service/returnService";
import ProtegaLogo from "@/assets/desktopLogo.png";

interface ReturnLoginProps {
  brandName?: string;
}

export default function ReturnLogin({ brandName = "" }: ReturnLoginProps) {
  const { merchantName } = useParams<{ merchantName: string }>();
  const [orderId, setOrderId] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [merchantLoading, setMerchantLoading] = useState(false);
  const [merchantData, setMerchantData] = useState<{
    name: string;
    logo?: string;
    termsUrl?: string;
  } | null>(null);

  const navigate = useNavigate();
  const { returnLogin } = useAuth();

  useEffect(() => {
    if (merchantName) {
      localStorage.setItem('merchant_portal_slug', merchantName);
    }
    const fetchMerchantDetails = async () => {
      if (!merchantName) return;

      setMerchantLoading(true);
      try {
        const response = await getMerchantProductCatalogue({ merchant_slug: merchantName });
        if (response.success && response.data.length > 0) {
          const merchant = response.data[0].merchants;
          setMerchantData({
            name: merchant.name,
            logo: merchant.logo_url,
            termsUrl: merchant.tnc_url || undefined
          });
        }
      } catch (err) {
        console.error("Failed to fetch merchant details:", err);
      } finally {
        setMerchantLoading(false);
      }
    };

    fetchMerchantDetails();
  }, [merchantName]);

  const displayBrandName = merchantData?.name || brandName;

  const handleLogin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    // Basic Validation
    if (!orderId.trim()) {
      toast.error("Please enter a valid Order ID");
      return;
    }
    
    // Combine country code and phone number
    const combinedMobile = `${countryCode}${phoneNumber}`.replace(/\s+/g, "");
    
    if (phoneNumber.length < 10) {
      toast.error("Please enter a valid 10-digit Mobile Number");
      return;
    }
    
    setIsLoading(true);
    try {
      const fullResponseData = await returnLogin(orderId.trim(), combinedMobile);
      
      // returnLogin returns restData which is { order_id } from data.data
      if (fullResponseData?.order_id) {
        navigate(`/customer/orders/${fullResponseData.order_id}`, { 
          state: { guestData: fullResponseData } 
        });
      } else {
        toast.error("Failed to retrieve order ID");
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Order not found. Please check your details.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /** Handle phone number change */
  const handlePhoneChange = (val: string) => {
    // Only allow digits, max 10
    const cleaned = val.replace(/\D/g, "").slice(0, 10);
    setPhoneNumber(cleaned);
  };

  return (
    <div className="tw-min-h-screen tw-flex tw-flex-col tw-bg-[#fafafa]">

      {/* Header */}
      <header className="tw-bg-[#1a1a1a] tw-py-8 tw-px-6 tw-flex tw-justify-center tw-items-center tw-min-h-[100px]">
        {merchantLoading ? (
          <div className="tw-h-8 tw-w-40 tw-skeleton-shimmer tw-rounded" />
        ) : merchantData?.logo ? (
          <img 
            src={merchantData.logo} 
            alt={displayBrandName} 
            className="tw-h-8 tw-w-auto tw-max-w-56 tw-object-contain" 
          />
        ) : (
          <p className="tw-text-white tw-text-lg tw-font-semibold tw-tracking-[0.4em] tw-uppercase">
            {displayBrandName}
          </p>
        )}
      </header>

      {/* Main */}
      <main className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-p-6 sm:tw-p-12">
        <div className="tw-bg-white tw-shadow-[0px_0px_40px_rgba(0,0,0,0.03)] tw-w-full tw-max-w-2xl tw-p-10 sm:tw-p-20">
          <h1 className="tw-text-[28px] sm:tw-text-[32px] tw-font-[800] tw-text-[#1a1a1a] tw-text-center tw-mb-14 tw-tracking-tight">
            Return/Exchange Order
          </h1>

          <form onSubmit={handleLogin} className="tw-space-y-6 tw-max-w-[420px] tw-mx-auto">
            {/* Order ID */}
            <div>
              <label className="tw-block tw-text-[11px] tw-font-bold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-mb-2">
                ENTER ORDER ID
              </label>
              <input
                type="text"
                required
                placeholder="e.g. 12345678"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                className="tw-w-full tw-bg-white tw-border tw-border-[#eaeaea] tw-px-4 tw-py-4 tw-text-[#1a1a1a] tw-text-sm focus:tw-outline-none focus:tw-border-gray-500 focus:tw-ring-1 focus:tw-ring-gray-200 tw-transition-all placeholder:tw-text-gray-300"
              />
            </div>

            {/* Mobile (Country Code + Phone) */}
            <div>
              <label className="tw-block tw-text-[11px] tw-font-bold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-mb-2">
                ENTER MOBILE NO
              </label>
              <div className="tw-flex tw-gap-3">
                <input
                  type="text"
                  required
                  placeholder="+91"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="tw-w-20 tw-bg-white tw-border tw-border-[#eaeaea] tw-px-3 tw-py-4 tw-text-[#1a1a1a] tw-text-sm tw-text-center focus:tw-outline-none focus:tw-border-gray-500 tw-transition-all placeholder:tw-text-gray-300"
                />
                <input
                  type="tel"
                  required
                  placeholder="10-digit mobile"
                  value={phoneNumber}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  maxLength={10}
                  className="tw-flex-1 tw-bg-white tw-border tw-border-[#eaeaea] tw-px-4 tw-py-4 tw-text-[#1a1a1a] tw-text-sm focus:tw-outline-none focus:tw-border-gray-500 tw-transition-all placeholder:tw-text-gray-300"
                />
              </div>
            </div>

            {/* Login Button */}
            <div className="tw-pt-3">
              <button 
                type="submit"
                disabled={isLoading}
                className="tw-w-full tw-py-4 tw-bg-[#1a1a1a] tw-text-white tw-font-bold tw-text-[11px] tw-uppercase tw-tracking-[0.15em] tw-border-none tw-cursor-pointer hover:tw-bg-black tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                {isLoading ? <Loader className="tw-animate-spin tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-mx-auto" /> : "INITIATE RETURN"}
              </button>
            </div>
          </form>
        </div>
      </main>

      {/* Footer */}
      <footer className="tw-bg-[#1a1a1a] tw-px-6 sm:tw-px-12 tw-py-8">
        <div className="tw-max-w-4xl tw-mx-auto tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-gap-8">
          {/* Policies */}
          <div>
            <p className="tw-text-xs tw-font-bold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-3">Policies</p>
            <ul className="tw-space-y-2">
              <li>
                <button 
                  onClick={() => {
                    if (merchantData?.termsUrl) {
                      window.open(merchantData.termsUrl, '_blank');
                    } else {
                      navigate("/lovepangolincare");
                    }
                  }} 
                  className="tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-sm tw-text-gray-400 hover:tw-text-white tw-transition-colors text-left"
                >
                  Return Policy
                </button>
              </li>
              <li><button onClick={() => navigate("/terms")} className="tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-sm tw-text-gray-400 hover:tw-text-white tw-transition-colors">Terms & Conditions</button></li>
              <li><button onClick={() => navigate("/contact-us")} className="tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-sm tw-text-gray-400 hover:tw-text-white tw-transition-colors">Contact support</button></li>
            </ul>
          </div>

          {/* Get in Touch */}
          <div>
            <p className="tw-text-xs tw-font-bold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-3">Get in Touch</p>
            <ul className="tw-space-y-2">
              <li className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-400">
                <Mail className="tw-w-4 tw-h-4 tw-flex-shrink-0" /> support@protega.ai
              </li>
              <li className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-400">
                <Phone className="tw-w-4 tw-h-4 tw-flex-shrink-0" /> +91 93240 95283
              </li>
              <li className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-400">
                <Clock className="tw-w-4 tw-h-4 tw-flex-shrink-0" /> Mon-Sat 9am - 7pm
              </li>
            </ul>
          </div>

          {/* Powered by */}
          <div className="tw-flex sm:tw-items-end">
            <div>
              <p className="tw-text-xs tw-text-gray-500 tw-uppercase tw-tracking-widest tw-mb-2">Powered by</p>
              <img src={ProtegaLogo} alt="Powered by Protega" className="tw-h-10 tw-w-auto tw-ml-[-16px] tw-object-contain" />
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}
